import { useEffect, useState } from 'react';
import {
    XMarkIcon,
    LockOpenIcon,
    LockClosedIcon,
} from '@heroicons/react/24/outline';
import SmallLoader from '../../../Common/components/SmallLoader';
import { cn } from '../../../lib/utils';
import { LibraryItem } from './hooks/useLibrary';

interface ResizeModalProps {
    asset: LibraryItem;
    onClose: () => void;
    onResize: (width: number, height: number) => Promise<void>;
}

export default function ResizeModal({
    asset,
    onClose,
    onResize,
}: ResizeModalProps) {
    const [width, setWidth] = useState<number>(0);
    const [height, setHeight] = useState<number>(0);
    const [originalWidth, setOriginalWidth] = useState<number>(0);
    const [originalHeight, setOriginalHeight] = useState<number>(0);
    const [aspectRatioLocked, setAspectRatioLocked] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [isResizing, setIsResizing] = useState(false);
    const [error, setError] = useState<string>('');

    // Load image dimensions
    useEffect(() => {
        const img = new Image();
        img.onload = () => {
            setOriginalWidth(img.width);
            setOriginalHeight(img.height);
            setWidth(img.width);
            setHeight(img.height);
            setIsLoading(false);
        };
        img.onerror = () => {
            setError('Failed to load image dimensions');
            setIsLoading(false);
        };
        img.src = asset.url;
    }, [asset.url]);

    const aspectRatio = originalWidth / originalHeight;

    const handleWidthChange = (newWidth: number) => {
        setWidth(newWidth);
        if (aspectRatioLocked && aspectRatio) {
            setHeight(Math.round(newWidth / aspectRatio));
        }
    };

    const handleHeightChange = (newHeight: number) => {
        setHeight(newHeight);
        if (aspectRatioLocked && aspectRatio) {
            setWidth(Math.round(newHeight * aspectRatio));
        }
    };

    const handleResize = async () => {
        // Validation
        if (width <= 0 || height <= 0) {
            setError('Width and height must be positive numbers');
            return;
        }

        // Prevent upscaling
        if (width > originalWidth || height > originalHeight) {
            setError(
                'Cannot upscale image. Maximum dimensions: ' +
                    originalWidth +
                    '×' +
                    originalHeight,
            );
            return;
        }

        // Check if dimensions changed
        if (width === originalWidth && height === originalHeight) {
            setError('Please change at least one dimension');
            return;
        }

        setError('');
        setIsResizing(true);

        try {
            await onResize(width, height);
            onClose();
        } catch (err) {
            setError('Failed to resize image. Please try again.');
            setIsResizing(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 flex justify-center items-center z-50"
            onClick={(e) => {
                e.stopPropagation();
                if (!isResizing) onClose();
            }}
        >
            <div
                className="bg-white dark:bg-[#262C2C] rounded-lg shadow-lg w-[500px] p-6"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-black dark:text-white">
                            Resize Image
                        </h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {asset.name}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={isResizing}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                {isLoading ? (
                    <div className="flex justify-center py-8">
                        <SmallLoader />
                    </div>
                ) : (
                    <>
                        {/* Original Dimensions */}
                        <div className="mb-6 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Original Dimensions:{' '}
                                <span className="font-bold">
                                    {originalWidth} × {originalHeight}
                                </span>
                            </p>
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                Aspect Ratio:{' '}
                                {aspectRatio ? aspectRatio.toFixed(2) : 'N/A'}
                            </p>
                        </div>

                        {/* Dimension Inputs */}
                        <div className="space-y-4 mb-6">
                            <div className="flex items-center gap-4">
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Width (px)
                                    </label>
                                    <input
                                        type="number"
                                        value={width}
                                        onChange={(e) =>
                                            handleWidthChange(
                                                parseInt(e.target.value) || 0,
                                            )
                                        }
                                        disabled={isResizing}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white"
                                        min="1"
                                        max={originalWidth}
                                    />
                                </div>

                                <div className="flex items-end pb-2">
                                    <button
                                        onClick={() =>
                                            setAspectRatioLocked(
                                                !aspectRatioLocked,
                                            )
                                        }
                                        disabled={isResizing}
                                        className={cn(
                                            'p-2 rounded-md transition-colors',
                                            aspectRatioLocked
                                                ? 'bg-black dark:bg-white text-white dark:text-black'
                                                : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
                                        )}
                                        title={
                                            aspectRatioLocked
                                                ? 'Unlock aspect ratio'
                                                : 'Lock aspect ratio'
                                        }
                                    >
                                        {aspectRatioLocked ? (
                                            <LockClosedIcon className="w-5 h-5" />
                                        ) : (
                                            <LockOpenIcon className="w-5 h-5" />
                                        )}
                                    </button>
                                </div>

                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Height (px)
                                    </label>
                                    <input
                                        type="number"
                                        value={height}
                                        onChange={(e) =>
                                            handleHeightChange(
                                                parseInt(e.target.value) || 0,
                                            )
                                        }
                                        disabled={isResizing}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white"
                                        min="1"
                                        max={originalHeight}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 rounded-md">
                                <p className="text-sm text-red-700 dark:text-red-400">
                                    {error}
                                </p>
                            </div>
                        )}

                        {/* Preview */}
                        <div className="mb-6 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                New Dimensions:{' '}
                                <span className="font-bold">
                                    {width} × {height}
                                </span>
                            </p>
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                New filename will be:{' '}
                                <span className="font-mono">
                                    {asset.name.replace(
                                        /(\.[^.]+)$/,
                                        `-${width}x${height}$1`,
                                    )}
                                </span>
                            </p>
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={onClose}
                                disabled={isResizing}
                                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleResize}
                                disabled={isResizing}
                                className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-md hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50 flex items-center gap-2"
                            >
                                {isResizing ? (
                                    <>
                                        <SmallLoader />
                                        <span>Resizing...</span>
                                    </>
                                ) : (
                                    'Resize'
                                )}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
